import { SCACEnum } from '@app/app.enum';
import environment from '@app/environments/environment';
import * as models from '@app/models/index';
import { ICarrierConfiguration } from '@app/models/index';
import axios, { CancelToken } from 'axios';
import { Injectable } from 'injection-js';

@Injectable()
export class AdminCarrierService {
	private baseUrl = environment.apiGateway.apiUrl;
	private adminCarrierApiUrl = '';

	constructor() {
		this.adminCarrierApiUrl = this.baseUrl + 'AdminCarrier/';
	}

	/**
	 * Get Carrier For Admin
	 * @param carrierId
	 * @param cancelToken
	 */
	public async getCarrier(carrierId: string, cancelToken?: CancelToken): Promise<models.ICarrier> {
		try {
			const url = this.adminCarrierApiUrl + `${carrierId}`;
			const res = await axios.get<models.ICarrier>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get All Carrier For Admin
	 * @param accountType
	 * @param cancelToken
	 */
	public async getAllCarriersForAdmin(scac: SCACEnum, cancelToken?: CancelToken): Promise<models.ICarrier[]> {
		try {
			const url = this.adminCarrierApiUrl;
			const res = await axios.get<models.ICarrier[]>(url, {
				cancelToken,
				params: {
					scac,
				},
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update Carrier For Admin
	 * @param data
	 * @param cancelToken
	 */
	public async updateCarrier(data: models.ICarrier, cancelToken?: CancelToken): Promise<models.ICarrier> {
		try {
			const url = this.adminCarrierApiUrl;
			const res = await axios.put<models.ICarrier>(url, data, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get Services For Admin
	 * @param carrierId
	 * @param cancelToken
	 */
	public async getCarrierServices(carrierId: string, cancelToken?: CancelToken): Promise<models.IService[]> {
		try {
			const url = `${this.adminCarrierApiUrl}${carrierId}/Service`;
			const res = await axios.get<models.IService[]>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get PackageType For Admin
	 * @param carrierId
	 * @param cancelToken
	 */
	public async getCarrierPackageTypes(carrierId: string, cancelToken?: CancelToken): Promise<models.IPackageType[]> {
		try {
			const url = `${this.adminCarrierApiUrl}${carrierId}/PackageType`;
			const res = await axios.get<models.IPackageType[]>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get SpecialService For Admin
	 * @param carrierId
	 * @param cancelToken
	 */
	public async getCarrierSpecialServices(
		carrierId: string,
		cancelToken?: CancelToken
	): Promise<models.ISpecialService[]> {
		try {
			const url = `${this.adminCarrierApiUrl}${carrierId}/SpecialService`;
			const res = await axios.get<models.ISpecialService[]>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update Service For Admin
	 * @param data
	 * @param cancelToken
	 */
	public async updateService(data: models.IServiceUpdateRequest, cancelToken?: CancelToken): Promise<models.IService> {
		try {
			const url = this.adminCarrierApiUrl + 'Service';
			const res = await axios.put<models.IService>(url, data, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update SpecialService For Admin
	 * @param data
	 * @param cancelToken
	 */
	public async updateSpecialService(
		data: models.IServiceUpdateRequest,
		cancelToken?: CancelToken
	): Promise<models.ISpecialService> {
		try {
			const url = this.adminCarrierApiUrl + 'SpecialService';
			const res = await axios.put<models.ISpecialService>(url, data, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * update PackageType For Admin
	 * @param data
	 * @param cancelToken
	 */
	public async updatePackageType(
		data: models.IServiceUpdateRequest,
		cancelToken?: CancelToken
	): Promise<models.IPackageType> {
		try {
			const url = this.adminCarrierApiUrl + 'PackageType';
			const res = await axios.put<models.IPackageType>(url, data, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async updateCarrierConfiguration(
		carrierId: string,
		configs: ICarrierConfiguration,
		cancelToken?: CancelToken
	): Promise<models.ICarrier> {
		try {
			const url = this.adminCarrierApiUrl + `${carrierId}/Configuration`;
			const res = await axios.put<models.ICarrier>(url, configs, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get fedEx-Native last imported Tracking Range Date For Admin
	 * @param cancelToken
	 */
	public async GetFedexNativeLastImportedTrackingRangeDate(
		cancelToken?: CancelToken
	): Promise<models.IFedExNativeLastImportedTrackingRangeDateVM> {
		try {
			const url = this.adminCarrierApiUrl + 'FedExNativeLastImportedRange';
			const res = await axios.get<models.IFedExNativeLastImportedTrackingRangeDateVM>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get last refreshed date of induct cache
	 * @param cancelToken
	 */
	public async GetEntryFailityZipMappingRefreshedDate(
		cancelToken?: CancelToken
	): Promise<models.IEntryFacilityZipRefreshedDateVM> {
		try {
			const url = this.adminCarrierApiUrl + 'EntryFacilityZipMapping/LastRefreshedDate';
			const res = await axios.get<models.IEntryFacilityZipRefreshedDateVM>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Refresh induct zip mapping data in db and in-mem cache
	 * @param cancelToken
	 */
	public async RefreshEntryFacilityZipMapping(cancelToken?: CancelToken): Promise<void> {
		try {
			const url = this.adminCarrierApiUrl + 'EntryFacilityZipMapping/Refresh';
			await axios.put(url, {
				cancelToken,
			});
		} catch (error) {
			throw error;
		}
	}
}



// WEBPACK FOOTER //
// ./src/services/admin-carrier.service.ts